import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import ProgramDetail from "./ProgramDetail";
import { PROGRAMS } from "@/data/content";

export default function Programs() {
  const [active, setActive] = useState(null);

  return (
    <section id="programs" className="py-24 sm:py-32 bg-ivory" data-testid="programs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Six Specialised Health Pathways"
          title="One Mission. Six Specialised Health Pathways."
          sub="Every health journey is different. That's why Aarogyam Bharat offers specialised pathways designed around different health goals and challenges."
        />

        <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory no-scrollbar lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
          {PROGRAMS.map((p, i) => (
            <Reveal
              key={p.slug}
              delay={i * 0.07}
              className="min-w-[85%] sm:min-w-[60%] lg:min-w-0 snap-center"
            >
              <motion.article
                whileHover={{ y: -10 }}
                transition={{ duration: 0.4 }}
                className="h-full bg-cardbg border border-botanical/10 rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-shadow duration-300 flex flex-col"
                data-testid={`program-card-${p.slug}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 0.6 }}
                    src={p.image}
                    alt={`${p.name} — ${p.subtitle}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
                  <span
                    className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full text-forest"
                    style={{ backgroundColor: p.accent }}
                  >
                    Pathway {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl sm:text-[1.7rem] text-forest leading-tight">{p.name}</h3>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.15em] text-sage">{p.subtitle}</p>
                  <p className="mt-4 font-serif italic text-lg text-botanical leading-snug">"{p.tagline}"</p>
                  <p className="mt-3 text-sm leading-relaxed text-inkmuted flex-1">{p.description}</p>
                  <button
                    onClick={() => setActive(p)}
                    data-testid={`program-explore-${p.slug}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-wide text-forest hover:text-sage transition-colors group self-start"
                  >
                    EXPLORE {p.name.replace("™", "").toUpperCase()}
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-14">
          <div className="max-w-3xl mx-auto text-center bg-sagesurface/60 border border-botanical/10 rounded-3xl px-8 py-8">
            <p className="font-serif italic text-xl sm:text-2xl text-forest leading-relaxed text-balance">
              "You don't have to figure out which pathway is right for you. We'll understand your needs during
              your Discovery Call and guide you toward the most suitable coaching pathway."
            </p>
          </div>
        </Reveal>
      </div>

      <ProgramDetail program={active} onClose={() => setActive(null)} />
    </section>
  );
}
